import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faCreditCard } from "@fortawesome/free-regular-svg-icons";
import {
  faAdd,
  faCancel,
  faChevronRight,
  faClose,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useInfiniteQueryMemberInvoiceListNew,
  useMemberInvoice,
  useMutationMemberInvoice,
  useQueryMemberPaymentMethodListNew,
} from "@gymflow/api";
import { isMobile } from "@gymflow/common";
import { cn, downloadPdfBlob, formatCurrency } from "@gymflow/helpers";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { useAddPaymentMethodAlert } from "apps/portal/src/hooks/useAddPaymentMethodAlert";
import { RouteFeature, RouteLayout } from "apps/portal/src/routes";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import { useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWindowSize } from "usehooks-ts";

import { usePageSize, usePortalRoutes } from "../../../hooks";
import useScaModal from "../../../hooks/useScaModal";
import { ModalContext, useClubSettings } from "../../../providers";
import { ApiResolverContext } from "../../../providers/ApiResolverProvider/ApiResolverContext";
import useGymflowModels from "../../../store";
import {
  Badge,
  Button,
  DownloadIcon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  RefreshIcon,
} from "../../atoms";
import { DotBadge } from "../../atoms/DotBadge";
import { PencilIcon } from "../../atoms/icons/PencilIcon";
import { PaymentMethodDetails } from "../../molecules/PaymentMethodDetails";
import {
  CollectPastDueInvoiceSidebarProviderContext,
  PaginatedTable,
} from "../../organisms";
import { ConfirmModal } from "../../templates";
import { PaymentsSidebarProviderContext } from "./components/PaymentsSidebarProviderContext";
import { StatusToColor } from "./constants";
import { normalizePaymentStatusLabel } from "./helpers";
import { UserMemberAdjustInvoiceModal } from "./UserMemberAdjustInvoiceModal";
import { UserMemberHighlightedInvoice } from "./UserMemberHighlightedInvoice";
import { UserMemberPaymentMethodSidebar } from "./UserMemberPaymentMethodSidebar";
import { UserMemberPaymentsCard } from "./UserMemberPaymentsCard";
import UserMemberWallet from "./UserMemberWallet";

export type UserMemberPaymentsProps = {
  memberId: string;
};

export const UserMemberPayments = ({ memberId }: UserMemberPaymentsProps) => {
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { club, featureFlags } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { openCollectPastDueInvoiceSidebar } = useContext(
    CollectPastDueInvoiceSidebarProviderContext,
  );
  const { openUserStaffPaymentsSidebar } = useContext(
    PaymentsSidebarProviderContext,
  );

  const showScaModal = useScaModal({
    asMember: false,
  });
  const { data: member } = useQuery(
    apiV2.staff.member.queries.idDeprecated(memberId),
  );
  const [currentPage, setCurrentPage] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const pageSize = usePageSize({
    tableContainerRef,
    rowHeight: 56,
  });
  const {
    data: invoices,
    isFetching,
    fetchNextPage,
  } = useInfiniteQueryMemberInvoiceListNew({
    api,
    memberId,
    params: { limit: pageSize },
  });

  const {
    refundInvoiceMutation,
    writeOffInvoiceMutation,
    cancelAuthorizationMutation,
  } = useMemberInvoice({ api, memberId });
  const { stackModal, hide } = useContext(ModalContext);
  const columnHelper =
    createColumnHelper<
      NonNullable<typeof invoices>["pages"][number]["content"][number]
    >();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [parent] = useAutoAnimate();
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } =
    useQueryMemberPaymentMethodListNew({
      api,
      memberId,
      clubId,
    });

  const defaultPaymentMethod =
    paymentMethods?.find((e) => e.defaultPaymentMethod) ?? paymentMethods?.[0];
  const showAddPaymentMethodAlert = useAddPaymentMethodAlert({
    userMemberId: memberId,
    asMember: false,
  });
  const { mutateAsync: downloadInvoice } = useMutationMemberInvoice({
    api,
  });

  const totalAmountColumn = columnHelper.display({
    header: "Amount",
    enableSorting: false,
    cell: (column) => {
      const invoice = column.row.original;
      return (
        <div className="flex flex-col items-start py-2">
          <div className="font-bold text-gray-950">
            {formatCurrency(invoice.totalAmount, club.defaultCurrency)}
          </div>
          <div className="text-sm text-gray-300">
            {formatCurrency(invoice.dueAmount, club.defaultCurrency)}
          </div>
        </div>
      );
    },
  });
  const issueDateColumn = columnHelper.accessor("issueDate", {
    header: "Date",
    enableSorting: false,
    cell: (column) => {
      return (
        <div>
          {DateTime.fromISO(column.getValue()).toLocaleString(
            DateTime.DATE_MED,
          )}
        </div>
      );
    },
  });

  const statusColumn = columnHelper.accessor("status", {
    header: "Status",
    enableSorting: false,
    cell: (column) => {
      const status = column.getValue();
      const errorCode = column.row.original.lastPaymentErrorCode;
      return (
        <DotBadge
          tooltip={
            status === "PAST_DUE"
              ? t(`StripeErrorCodes.Short.${errorCode ?? "UNKNOWN"}`)
              : undefined
          }
          intent={StatusToColor[status]}
        >
          {normalizePaymentStatusLabel(status)}
        </DotBadge>
      );
    },
  });

  const actionColumn = columnHelper.display({
    id: "actions",
    enableSorting: false,
    cell: (column) => {
      const invoice = column.row.original;
      const actions = [];
      if (invoice.status) {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={async () => {
              const { number } = invoice;
              const { data: html } = await downloadInvoice({
                invoiceNumber: number,
              });
              downloadPdfBlob(html, `Gymflow-${number}.pdf`);
            }}
          >
            <DownloadIcon pathClassName="stroke-gray-500" className="size-4" />
            Download
          </Button>,
        );
      }
      if (invoice.status === "PAID" && invoice.totalAmount > 0) {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={async (e) => {
              e.stopPropagation();
              stackModal(
                <ConfirmModal
                  type="warning"
                  title="Refund Invoice"
                  onCancel={() => {
                    hide();
                  }}
                  onConfirm={async () => {
                    await refundInvoiceMutation.mutateAsync({
                      invoiceNumber: invoice.number,
                    });
                    hide();
                  }}
                >
                  Are you sure you want to refund this invoice?
                </ConfirmModal>,
              );
            }}
          >
            <RefreshIcon className="size-4" />
            Refund
          </Button>,
        );
      }
      if (invoice.status === "AWAITING_AUTHORIZATION") {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={async () => {
              await cancelAuthorizationMutation.mutateAsync({
                invoiceNumber: invoice.number,
              });
            }}
          >
            <FontAwesomeIcon icon={faClose} className="size-4" />
            Cancel Auth
          </Button>,
        );
      }
      if (invoice.status === "PAST_DUE") {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={() => {
              openCollectPastDueInvoiceSidebar({
                invoiceNumber: invoice.number,
                userMemberId: memberId,
              });
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} className="size-4" />
            Collect
          </Button>,
        );
      }
      if (invoice.status === "PAST_DUE" || invoice.status === "SCHEDULED") {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={async () => {
              if (!member) {
                return;
              }
              stackModal(
                <UserMemberAdjustInvoiceModal
                  invoice={invoice}
                  userMember={member}
                />,
              );
            }}
          >
            <PencilIcon className="size-4" />
            Adjust
          </Button>,
        );
      }
      if (invoice.status === "PAST_DUE") {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={async () => {
              await writeOffInvoiceMutation.mutateAsync({
                invoiceNumber: invoice.number,
              });
            }}
          >
            <FontAwesomeIcon icon={faCancel} className="size-4" />
            Write Off
          </Button>,
        );
      }
      if (invoice.status === "AWAITING_AUTHORIZATION") {
        actions.push(
          <Button
            intent="transparent"
            className="h-8 min-h-0 justify-start gap-2 p-2 py-0"
            onClick={() => {
              showScaModal({
                invoiceNumber: invoice.number,
                userMemberId: memberId,
              });
            }}
          >
            <FontAwesomeIcon icon={faCreditCard} className="size-4" />
            Authorize
          </Button>,
        );
      }
      if (actions.length === 0) {
        return;
      }
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button className="size-9 p-0">
              <FontAwesomeIcon
                icon={faEllipsisV}
                className="size-4 text-gray-950"
              />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex w-48 flex-col gap-1 p-2" align="end">
            {actions}
          </PopoverContent>
        </Popover>
      );
    },
  });

  const mobileColumns = [
    columnHelper.display({
      id: "date-and-price",
      header: "Amount",
      enableSorting: false,
      cell: (column) => {
        const invoice = column.row.original;
        return (
          <div className="flex flex-col items-start py-2">
            <div className="text-gray-950">
              {DateTime.fromISO(invoice.issueDate).toLocaleString(
                DateTime.DATE_MED,
              )}
            </div>
            <div className="font-bold text-gray-950">
              {formatCurrency(invoice.totalAmount, club.defaultCurrency)}
            </div>
            <div className="text-sm text-gray-300">
              {formatCurrency(invoice.dueAmount, club.defaultCurrency)}
            </div>
          </div>
        );
      },
    }),
    statusColumn,
    actionColumn,
  ];
  const desktopColumns = [
    issueDateColumn,
    totalAmountColumn,
    columnHelper.accessor("promotionCode", {
      header: "Promotion Code",
      enableSorting: false,
      cell: (column) => {
        return <Badge>{column.getValue() ?? "-"}</Badge>;
      },
    }),
    statusColumn,
    actionColumn,
  ];

  const windowSize = useWindowSize({ debounceDelay: 100 });
  const currentPageInvoices = invoices?.pages[currentPage]?.content ?? [];
  const { createClubLink } = usePortalRoutes();
  const { data: currentWalletBalance, isLoading } = useQuery(
    apiV2.staff.wallet.queries.list(member?.id),
  );

  return (
    <>
      {member && (
        <UserMemberPaymentMethodSidebar
          isOpen={isSidebarOpen}
          hide={() => setIsSidebarOpen(false)}
          userMember={member}
        />
      )}
      <div
        ref={parent}
        className="-m-4 flex h-full flex-col gap-4 overflow-y-auto overflow-x-hidden p-4"
      >
        <div
          className="flex min-h-40 gap-4 overflow-x-auto overflow-y-hidden"
          style={{ scrollbarWidth: "thin" }}
        >
          {member && <UserMemberHighlightedInvoice userMember={member} />}
          {featureFlags.FE_USER_WALLETS && (
            <UserMemberPaymentsCard.Container isLoading={isLoading}>
              <UserMemberWallet
                currentWalletBalance={currentWalletBalance}
                link={createClubLink(
                  RouteLayout.Staff,
                  RouteFeature.UserMemberWalletHistory.replace(":id", memberId),
                )}
                memberId={memberId}
              />
            </UserMemberPaymentsCard.Container>
          )}
          <UserMemberPaymentsCard.Container isLoading={isLoadingPaymentMethods}>
            <UserMemberPaymentsCard.Header>
              <div className="flex min-w-0 flex-row items-center gap-2">
                <div className="bg-lightblue-100 flex size-9 items-center justify-center rounded-full">
                  <FontAwesomeIcon
                    icon={faCreditCard}
                    className="text-lightblue-500"
                  />
                </div>
                <div className="min-w-0 flex-1 truncate font-semibold text-gray-950">
                  Default Payment Method
                </div>
              </div>
              {paymentMethods && paymentMethods.length > 0 && (
                <Button
                  onClick={() => setIsSidebarOpen(true)}
                  intent="transparent"
                  className="h-9 items-center gap-2"
                >
                  <div className="text-nowrap">View All</div>
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              )}
            </UserMemberPaymentsCard.Header>
            <UserMemberPaymentsCard.Footer>
              {paymentMethods && paymentMethods.length === 0 && (
                <Button
                  onClick={() => {
                    showAddPaymentMethodAlert();
                  }}
                  intent="secondary-outline"
                  className="mt-4 flex w-full flex-row gap-1"
                >
                  <FontAwesomeIcon
                    icon={faAdd}
                    className="text-secondary-500"
                  />
                  Add Payment Method
                </Button>
              )}
              {defaultPaymentMethod && (
                <div
                  className="flex h-16 flex-1 flex-row items-center justify-between rounded-xl border border-gray-200 px-4 py-3"
                  key={defaultPaymentMethod.id}
                >
                  <PaymentMethodDetails
                    paymentMethod={defaultPaymentMethod}
                    userMemberId={memberId}
                    as="staff"
                  />
                </div>
              )}
            </UserMemberPaymentsCard.Footer>
          </UserMemberPaymentsCard.Container>
        </div>
        <div className="flex h-full min-h-96 flex-col gap-4">
          <div className="text-lg font-semibold text-gray-950">Invoices</div>
          <PaginatedTable
            tableProps={{
              data: currentPageInvoices,
              columns: !isMobile(windowSize.width)
                ? desktopColumns
                : mobileColumns,
              isFetching: isFetching,
              pageSize: pageSize,
              tableContainerRef,
            }}
            hasPreviousPage={!!invoices && currentPage > 0}
            hasNextPage={
              !!invoices && !!invoices.pages[currentPage]?.nextPageToken
            }
            goToNextPage={() => {
              setCurrentPage((e) => e + 1);
              if (!invoices?.pages[currentPage + 1]) {
                fetchNextPage();
              }
            }}
            goToPreviousPage={() => {
              setCurrentPage((e) => e - 1);
            }}
            onRowClick={(e, row) => {
              if ((e.target as HTMLElement).closest("button")) return;
              const invoiceNumber = row.original.number;
              if (invoiceNumber) {
                openUserStaffPaymentsSidebar({ invoiceNumber, memberId });
              }
            }}
            rowClassName={cn(
              "border-gray-200 first:border-t-0 transition-colors duration-150",
              "cursor-pointer hover:bg-gray-50 active:bg-gray-100",
            )}
          />
        </div>
      </div>
    </>
  );
};
